'use client';
import { getFeatureFlagForAnonymous } from '@storefront/lib/launchdarkly';
import { getLogger } from '@storefront/logging';
import {
  DataValveCDMEvent,
  SimpleUiEvent,
} from '@storefront/types/webanalytics';
import sendOpenTelemetryEvent from '@storefront/utils/sendOpenTelemetryEvent';
import { getWebAnalyticsModule } from '@storefront/utils/webAnalytics';
import { env } from 'next-runtime-env';
import Cookies from 'universal-cookie';

import {
  KeyEvent,
  KeyEventsByPath,
  keyEventsByPath,
  keyEventsByRouteChange,
  Stack,
} from './types';
import { TRIGGERED_EVENTS_KEY } from './validateSDEventsByPage';

const eventDetailType =
  'com.hp.cdm.domain.telemetry.type.eventDetail.category.simpleUi.version.1';
const eventCategory = 'simpleUi';
const version = '1.4.0';
type OverridableProps =
  | 'screenName'
  | 'action'
  | 'controlName'
  | 'controlDetail'
  | 'actionAuxParams'
  | 'screenMode'
  | 'screenPath'
  | 'coreEvent';

const THREE_SECONDS_IN_MS = 3000;
const RETRY_ATTEMPTS = 3;
export type OverridablePropsWithValue = Partial<{
  [P in OverridableProps]: string | boolean;
}>;

const logger = getLogger('analytics');

type StackType = {
  [key: string]: any;
};

const StackTyped: StackType = Stack;

const webAnalyticsModule = getWebAnalyticsModule();

export default class DataCollection {
  private static activityOverride: string | null = null;
  private static _isSMB = false;

  public static setIsSMB(value: boolean) {
    this._isSMB = value;
  }

  public static setActivityOverride(activity: string | null) {
    this.activityOverride = activity;
  }

  private static async _sendEvent(
    event: SimpleUiEvent,
    overrideValue: OverridablePropsWithValue = {},
    eventTracingId?: string,
    retryCount = 0,
    tenantId?: string,
    stratusUserId?: string
  ): Promise<any> {
    if (!webAnalyticsModule) {
      return;
    }
    Object.assign(event, overrideValue);

    const cookieStore = new Cookies();
    const isSmbFromCookie =
      cookieStore.get('smb') === '1' ||
      (typeof document !== 'undefined' &&
        document.cookie
          .split(';')
          .some(
            (c) => c.trim().startsWith('smb=') && c.trim().slice(4) === '1'
          ));
    const smbFlagActive = this._isSMB || isSmbFromCookie;

    if (smbFlagActive) {
      if (!event.activity.startsWith('Smb')) {
        event.activity = `Smb${event.activity}`;
      }
    } else if (this.activityOverride) {
      event.activity = this.activityOverride;
    }

    const coreEvent = event?.coreEvent;

    // remove control property
    if (coreEvent !== undefined) {
      delete event.coreEvent;
    }

    const cdmEventObject: DataValveCDMEvent = {
      dateTime: new Date().toISOString(),
      eventDetailType: eventDetailType,
      eventCategory: eventCategory,
      version: version,
      eventDetail: event,
    };

    if (!webAnalyticsModule.isInited()) {
      console.log('WebAnalytics not available to publish events. Retrying..');

      if (retryCount >= RETRY_ATTEMPTS) {
        console.error(
          'WebAnalytics not available to publish events. Retried 3 times (sendEvent).',
          JSON.stringify(event)
        );

        return;
      }

      return setTimeout(
        () =>
          this.sendEvent(
            event,
            overrideValue,
            eventTracingId,
            retryCount + 1,
            tenantId,
            stratusUserId
          ),
        THREE_SECONDS_IN_MS
      ); // prevent sending event before initializing
    }
    const options = {
      metadata: {
        trackingIdentifier: eventTracingId ? `${eventTracingId}` : null,
        stratusUserId: stratusUserId,
      },
    };
    await webAnalyticsModule.publishCdmEvents([cdmEventObject], options);
    if (coreEvent) {
      this.recordEvent(event);
    }

    const isDebugMode = await getFeatureFlagForAnonymous(
      'isDebugMode',
      env('NEXT_PUBLIC_FF_IS_DEBUG_MODE') === 'true'
    );
    if (isDebugMode) {
      this.handleDebugMode(event, retryCount, tenantId);
    }
  }

  static recordEvent = (event: SimpleUiEvent) => {
    const page = window.location.pathname;
    const pageKey = Object.keys(keyEventsByPath).find((key) =>
      page.includes(key)
    );
    const pageKeyByRouteChange = Object.keys(keyEventsByRouteChange).find(
      (key) => page.includes(key)
    );

    if (!pageKey && !pageKeyByRouteChange) return;

    const {
      action,
      activity,
      screenName,
      screenPath,
      actionAuxParams,
      screenMode,
      controlName,
    } = event as KeyEvent;

    const eventsSent = localStorage.getItem(TRIGGERED_EVENTS_KEY) || '{}';
    const parsedEvents = JSON.parse(eventsSent) as KeyEventsByPath;
    const newEvent = {
      action,
      activity,
      screenName,
      screenPath,
      actionAuxParams,
      screenMode,
      controlName,
    };

    parsedEvents[pageKey] = [...(parsedEvents[pageKey] || []), newEvent];
    localStorage.setItem(TRIGGERED_EVENTS_KEY, JSON.stringify(parsedEvents));
  };

  public static async sendEvent(
    event: SimpleUiEvent,
    overrideValue: OverridablePropsWithValue = {},
    eventTracingId?: string,
    retryCount = 0,
    tenantId?: string,
    stratusUserId?: string
  ): Promise<any> {
    try {
      if (!event.screenName && !overrideValue.screenName) {
        console.error('screenName is required to send an event.', event);
        return;
      }
      if (event.screenMode === '') {
        delete event.screenMode;
      }
      await this._sendEvent(
        event,
        overrideValue,
        eventTracingId,
        retryCount,
        tenantId,
        stratusUserId
      );
      return true;
    } catch (error) {
      console.error(
        'Failed to send analytics event using DataCollection.sendEvent method.',
        event,
        error
      );
    }
  }

  public static initAnalytics = async (
    stack: string,
    telemetryApiKey: string
  ) => {
    console.log('initializing analytics:', stack);
    try {
      if (typeof window === 'undefined') {
        return;
      }
      if (webAnalyticsModule.isInited()) {
        logger.info('Web Analytics already started');
        return;
      }
      logger.info('Starting Web Analytics');
      await webAnalyticsModule.init({
        enableOneTrust: true,
        enabled: true,
        assetType: 'ws-hp.com-magento-storefront', //^$|[0-9a-zA-Z-.]+
        telemetryAPIkey: telemetryApiKey,
        stack: StackTyped[stack] ?? StackTyped.local,
        // authProvider: { getAccessToken: () => '<USER-TOKEN>' }, // it can be a promise as well.
      });

      logger.info('Web Analytics started');
      this.handleStartGlobalVariables();
    } catch (error) {
      console.error('Failed to init web-analytics-bundle.', error);
    }
  };

  public static handleStartGlobalVariables = () => {
    window.webAnalyticsScriptStarted = true;
  };

  public static async handlePageLoadEvent(
    event: SimpleUiEvent,
    tracingId?: string,
    tenantId?: string,
    stratusUserId?: string,
    retryCount = 0
  ): Promise<any> {
    this.verifyScriptStatus(event);
    const { webAnalyticsScriptStarted } = window;
    if (!webAnalyticsScriptStarted) {
      if (retryCount >= RETRY_ATTEMPTS) {
        console.error(
          'WebAnalytics not available to publish events. Retried 3 times (handlePageLoadEvent).',
          JSON.stringify(event)
        );

        return;
      }

      return setTimeout(
        () =>
          this.handlePageLoadEvent(
            event,
            tracingId,
            tenantId,
            stratusUserId,
            retryCount + 1
          ),
        THREE_SECONDS_IN_MS
      );
    }
    if (webAnalyticsScriptStarted) {
      await this.sendEvent(event, null, tracingId, 0, tenantId, stratusUserId);
    }
  }

  public static handlePageUnloadEvent = async (
    event: SimpleUiEvent,
    callback?: () => void,
    tracingId?: string,
    tenantId?: string,
    stratusUserId?: string,
    retryCount = 0
  ): Promise<any> => {
    this.verifyScriptStatus(event);
    const { webAnalyticsScriptStarted } = window;

    if (!webAnalyticsScriptStarted) {
      if (retryCount >= RETRY_ATTEMPTS) {
        console.error(
          'WebAnalytics not available to publish events. Retried 3 times (handlePageUnloadEvent).',
          JSON.stringify(event)
        );

        return;
      }

      return setTimeout(
        () =>
          this.handlePageUnloadEvent(
            event,
            callback,
            tracingId,
            tenantId,
            stratusUserId,
            retryCount + 1
          ),
        THREE_SECONDS_IN_MS
      );
    }
    if (webAnalyticsScriptStarted) {
      await this.sendEvent(event, null, tracingId, 0, tenantId, stratusUserId);
      if (callback) callback();
    }
  };

  public static verifyScriptStatus = (event: SimpleUiEvent) => {
    const { webAnalyticsScriptStarted } = window;

    if (!webAnalyticsScriptStarted) {
      this.ensureWebAnalyticsInit();
      return console.error(
        'Web Analytics not started for the event: ',
        JSON.stringify(event)
      );
    }
  };

  private static readonly handleDebugMode = (
    event: SimpleUiEvent,
    retryCount: number,
    tenantId?: string
  ) => {
    const actionAuxParamsTest =
      !event.actionAuxParams ||
      /^([a-zA-Z_]\w*=[^&]*?)(&[a-zA-Z_]\w*=[^&]*?)*$/.test(
        event.actionAuxParams
      ); // regex v1.4.0
    const screenNameTest =
      !event.screenName || /^([A-Z][a-z]+)+?[0-9.]*$/.test(event.screenName); // regex v1.4.0

    const debugObject = {
      event,
      webAnalyticsScript: {
        screenNameTest,
        actionAuxParamsTest,
        started: window.webAnalyticsScriptStarted,
        debuggedState: webAnalyticsModule.getDebuggedState() || '',
      },
      retryCount,
    };
    const hasSchemaErrors = !screenNameTest || !actionAuxParamsTest;

    sendOpenTelemetryEvent(
      'web-analytics-send-event',
      JSON.stringify(debugObject),
      tenantId,
      hasSchemaErrors
    );
  };
  private static readonly ensureWebAnalyticsInit = async () => {
    if (!env('NEXT_PUBLIC_APP_ENV') || !env('NEXT_PUBLIC_API_TELEMETRY_KEY'))
      return;
    if (typeof window !== 'undefined') {
      await DataCollection.initAnalytics(
        env('NEXT_PUBLIC_APP_ENV'),
        env('NEXT_PUBLIC_API_TELEMETRY_KEY')
      );
    }
  };
}
